import { useId, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Bell, BellRing, Check, Search, Smartphone, Trash2, Undo2 } from 'lucide-react';
import { Abas, PainelAba } from '../componentes/abas';
import { Botao, Cartao, Segmentado, Selo } from '../componentes/base';
import { Carregando, Erro, Estado, Vazio } from '../componentes/estados';
import { Alternador, Entrada, Selecao } from '../componentes/formulario';
import { useDebounce } from '../componentes/tabela';
import { TextoCortado } from '../componentes/texto-cortado';
import { useToast } from '../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post } from '../nucleo/api';
import { dataHora } from '../nucleo/formato';
import { deveRepetir } from '../nucleo/recuo';
import { buscarNotificacoes, CATEGORIAS, tiposDaBusca, type TextosTipos, useMarcarLidas, useMarcarNaoLidas, useOpcoes, useRecarregarNoSinal, type Categoria, type Notificacao, type Opcoes } from '../nucleo/notificacoes/dados';
import { ativarPush, suportePush } from '../nucleo/notificacoes/push';
import { ItemNotificacao, useAbrirNotificacao } from '../nucleo/notificacoes/Sino';
import { AntecedenciaAvisos, useMostraAntecedencia } from '../nucleo/notificacoes/AntecedenciaAvisos';
import { SubtituloPagina } from '../componentes/subtitulo-pagina';
import { carregarMais } from '../nucleo/foco';
import { useIcones } from '../nucleo/icones-navegacao';
import { useVoltar } from '../nucleo/voltar';

interface EstadoCanal {
  ativo: boolean;
  padrao: boolean;
  excecao: boolean | null;
}
interface TipoPref {
  chave: string;
  categoria: Categoria;
  app: EstadoCanal;
  push: EstadoCanal;
}
interface Aparelho {
  id: string;
  nome: string;
  servico: 'google' | 'apple' | 'mozilla' | 'microsoft' | 'local';
  created_at: string;
  ultimo_envio_em: string | null;
}

/** Linha de opção: rótulo, ajuda e o alternador do núcleo (nome acessível pelo rótulo visível). */
function LinhaOpcao({ id, rotulo, ajuda, marcado, aoMudar, desabilitado }: { id: string; rotulo: string; ajuda?: string; marcado: boolean; aoMudar: (v: boolean) => void; desabilitado?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-tinta-1">
          {rotulo}
        </label>
        {ajuda && <p className="text-xs text-tinta-3">{ajuda}</p>}
      </div>
      <Alternador id={id} marcado={marcado} aoMudar={aoMudar} desabilitado={desabilitado} />
    </div>
  );
}

function Caixa() {
  const { t } = useTranslation();
  const [categoria, setCategoria] = useState<Categoria | ''>('');
  // CRM novo desenho, lote 0: Todas, Não lidas e Lidas (o pop-up do sino mostra só as não lidas; as lidas ficam aqui).
  const [situacao, setSituacao] = useState<'todas' | 'nao_lidas' | 'lidas'>('todas');
  const naoLidas = situacao === 'nao_lidas';
  const desmarcar = useMarcarNaoLidas();
  const [busca, setBusca] = useState('');
  const buscaD = useDebounce(busca).trim();
  const tiposBusca = buscaD ? tiposDaBusca(t('notificacoes.tipos', { returnObjects: true }) as TextosTipos, buscaD) : [];
  const marcar = useMarcarLidas();
  const abrir = useAbrirNotificacao();
  const toast = useToast();
  const q = useInfiniteQuery({
    queryKey: ['notificacoes', 'caixa', categoria, situacao, buscaD],
    queryFn: ({ pageParam }) =>
      buscarNotificacoes<{ itens: Notificacao[]; proximo_cursor: string | null }>('/notificacoes', {
        cursor: pageParam || undefined,
        categoria: categoria || undefined,
        nao_lidas: naoLidas || undefined,
        lidas: situacao === 'lidas' || undefined,
        busca: buscaD || undefined,
        tipos_busca: tiposBusca.length ? tiposBusca.join(',') : undefined,
      }),
    retry: deveRepetir,
    initialPageParam: '',
    getNextPageParam: (u) => u.proximo_cursor ?? undefined,
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  return (
    <Cartao
      titulo={t('notificacoes.abas.caixa')}
      acao={
        <Botao variante="fantasma" tamanho="sm" carregando={marcar.isPending} onClick={() => marcar.mutate({ todas: true }, { onError: (e) => toast.erro(mensagemDeErro(e)) })}>
          {t('notificacoes.marcarTodas')}
        </Botao>
      }
    >
      <div className="mb-3 grid gap-3 sm:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <label htmlFor="notif-busca" className="text-sm font-medium text-tinta-2">
            {t('comum.buscar')}
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
            <Entrada id="notif-busca" type="search" placeholder={t('notificacoes.buscarPlaceholder')} className="pl-9" maxLength={100} value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <label htmlFor="notif-categoria" className="text-sm font-medium text-tinta-2">
            {t('notificacoes.categoria')}
          </label>
          <Selecao id="notif-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value as Categoria | '')} vazio={t('notificacoes.todas')} opcoes={CATEGORIAS.map((c) => ({ valor: c, rotulo: t(`notificacoes.categorias.${c}`) }))} />
        </div>
        <div className="self-end">
          <Segmentado
            rotulo={t('notificacoes.situacao')}
            valor={situacao}
            aoMudar={setSituacao}
            data-situacao-notificacoes=""
            opcoes={(['todas', 'nao_lidas', 'lidas'] as const).map((v) => ({ valor: v, rotulo: t(`notificacoes.situacoes.${v}`) }))}
          />
        </div>
      </div>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : itens.length === 0 ? (
        <Vazio
          titulo={buscaD ? t('notificacoes.vazioBusca') : naoLidas ? t('notificacoes.vazioNaoLidas') : situacao === 'lidas' ? t('notificacoes.vazioLidas') : t('notificacoes.vazioTitulo')}
          texto={buscaD ? undefined : t('notificacoes.vazioTexto')}
          icone={<Bell className="size-5" aria-hidden />}
        />
      ) : (
        <>
          <ul className="flex flex-col divide-y divide-borda">
            {itens.map((n) => (
              <li key={n.id} className="flex items-start gap-2 py-1">
                <div className="min-w-0 flex-1">
                  <ItemNotificacao n={n} aoAbrir={abrir} />
                </div>
                <span className="mt-2 shrink-0">
                  <Selo>{t(`notificacoes.categorias.${n.categoria}`)}</Selo>
                </span>
                <button
                  type="button"
                  data-alternar-lida={n.id}
                  aria-label={n.lida_em ? t('notificacoes.marcarNaoLida') : t('notificacoes.marcarLida')}
                  title={n.lida_em ? t('notificacoes.marcarNaoLida') : t('notificacoes.marcarLida')}
                  disabled={marcar.isPending || desmarcar.isPending}
                  onClick={() => (n.lida_em ? desmarcar : marcar).mutate({ ids: [n.id] }, { onError: (e) => toast.erro(mensagemDeErro(e)) })}
                  className="mt-1 flex size-9 shrink-0 items-center justify-center rounded-lg text-tinta-3 hover:bg-sup-3 hover:text-tinta-1 disabled:opacity-50"
                >
                  {n.lida_em ? <Undo2 className="size-4" aria-hidden /> : <Check className="size-4" aria-hidden />}
                </button>
              </li>
            ))}
          </ul>
          {q.hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Botao variante="secundario" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
                {t('notificacoes.carregarMais')}
              </Botao>
            </div>
          )}
        </>
      )}
    </Cartao>
  );
}

function Preferencias() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const prefs = useQuery({ queryKey: ['notificacoes', 'preferencias'], queryFn: () => get<{ tipos: TipoPref[] }>('/notificacoes/preferencias') });
  const mudar = useMutation({
    mutationFn: (e: { tipo: string; canal: 'app' | 'push'; ativo: boolean | null }) => api('PUT', '/notificacoes/preferencias', e),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notificacoes', 'preferencias'] }),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Lote de 21/10 (item 2): "Voltar tudo ao padrão" apaga as exceções da pessoa de uma vez.
  const zerar = useMutation({
    mutationFn: () => post<{ removidas: number }>('/notificacoes/preferencias/padrao'),
    onSuccess: async (r) => {
      toast.sucesso(t('notificacoes.voltouTudo', { count: r.removidas }));
      await qc.invalidateQueries({ queryKey: ['notificacoes', 'preferencias'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const temExcecao = !!prefs.data?.tipos.some((tp) => tp.app.excecao !== null || tp.push.excecao !== null);
  return (
    <Cartao
      titulo={t('notificacoes.prefsTitulo')}
      acao={
        <Botao variante="fantasma" tamanho="sm" disabled={!temExcecao} carregando={zerar.isPending} onClick={() => zerar.mutate()}>
          {t('notificacoes.voltarTudo')}
        </Botao>
      }
    >
      <p className="mb-3 text-sm text-tinta-3">{t('notificacoes.prefsTexto')}</p>
      <Estado consulta={prefs}>
        {(d) => (
          <ul className="flex flex-col divide-y divide-borda">
            {[...d.tipos].sort((a, b) => CATEGORIAS.indexOf(a.categoria) - CATEGORIAS.indexOf(b.categoria)).map((tp) => (
              <li key={tp.chave} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <TextoCortado className="block text-sm font-medium text-tinta-1">{t(`notificacoes.tipos.${tp.chave}.titulo`, { defaultValue: tp.chave })}</TextoCortado>
                  <span className="text-xs text-tinta-3">{t(`notificacoes.categorias.${tp.categoria}`)}</span>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  {(['app', 'push'] as const).map((canal) => (
                    <div key={canal} className="flex items-center gap-2">
                      <Alternador rotulo={`${t(`notificacoes.tipos.${tp.chave}.titulo`, { defaultValue: tp.chave })}: ${t(`notificacoes.canaisMeus.${canal}`)}`} marcado={tp[canal].ativo} aoMudar={(v) => mudar.mutate({ tipo: tp.chave, canal, ativo: v })} />
                      <span className="text-xs text-tinta-2">{t(`notificacoes.canaisMeus.${canal}`)}</span>
                      {tp[canal].excecao === null ? (
                        <Selo>{t('notificacoes.padrao')}</Selo>
                      ) : (
                        <button type="button" onClick={() => mudar.mutate({ tipo: tp.chave, canal, ativo: null })} className="text-xs text-acento-forte underline">
                          {t('notificacoes.voltarPadrao')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Estado>
    </Cartao>
  );
}

function OpcoesPessoais({ opcoes }: { opcoes: Opcoes }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  // Review r27 spec (M1, varredura): erro de mutação nunca em silêncio (o QueryClient não trata).
  const mudar = useMutation({
    mutationFn: (e: Partial<Pick<Opcoes, 'som' | 'novidades_popup'>>) => patch('/notificacoes/opcoes', e),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notificacoes', 'opcoes'] }),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Cartao titulo={t('notificacoes.opcoesTitulo')}>
      <div className="flex flex-col gap-4">
        <LinhaOpcao id="notif-som" rotulo={t('notificacoes.som')} ajuda={t('notificacoes.somAjuda')} marcado={opcoes.som} aoMudar={(v) => mudar.mutate({ som: v })} />
        <LinhaOpcao id="notif-novidades" rotulo={t('notificacoes.novidadesPopup')} ajuda={t('notificacoes.novidadesAjuda')} marcado={opcoes.novidades_popup} aoMudar={(v) => mudar.mutate({ novidades_popup: v })} />
      </div>
    </Cartao>
  );
}

function Aparelhos({ chave }: { chave: string | null }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const lista = useQuery({ queryKey: ['notificacoes', 'dispositivos'], queryFn: () => get<{ itens: Aparelho[] }>('/notificacoes/dispositivos') });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['notificacoes'] });
  const ativar = useMutation({
    mutationFn: () => ativarPush(chave!),
    onSuccess: (r) => (r === 'ok' ? (toast.sucesso(t('notificacoes.pushAtivado')), recarregar()) : toast.erro(t(r === 'negado' ? 'notificacoes.pushNegado' : 'notificacoes.pushSemSuporte'))),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({ mutationFn: (id: string) => del(`/notificacoes/dispositivos/${id}`), onSuccess: recarregar, onError: (e) => toast.erro(mensagemDeErro(e)) });
  const teste = useMutation({ mutationFn: () => post('/notificacoes/teste'), onSuccess: () => (toast.sucesso(t('notificacoes.testeEnviado')), recarregar()), onError: (e) => toast.erro(mensagemDeErro(e)) });
  const suporte = suportePush();
  const aviso = !chave ? t('notificacoes.pushIndisponivel') : suporte === 'ios_instalar' ? t('notificacoes.pushIos') : suporte === 'nao' ? t('notificacoes.pushSemSuporte') : suporte === 'negado' ? t('notificacoes.pushNegado') : null;
  return (
    <Cartao titulo={t('notificacoes.aparelhosTitulo')}>
      <div className="flex flex-col gap-3">
        {aviso && <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2">{aviso}</p>}
        <div className="flex flex-wrap gap-2">
          <Botao variante="primario" icone={<BellRing className="size-4" aria-hidden />} disabled={!!aviso} carregando={ativar.isPending} onClick={() => ativar.mutate()}>
            {t('notificacoes.ativar')}
          </Botao>
          <Botao variante="secundario" carregando={teste.isPending} onClick={() => teste.mutate()}>
            {t('notificacoes.enviarTeste')}
          </Botao>
        </div>
        <p className="text-xs text-tinta-3">{t('notificacoes.instalar')}</p>
        <Estado consulta={lista}>
          {(d) =>
            d.itens.length === 0 ? (
              <p className="text-sm text-tinta-3">{t('notificacoes.aparelhosVazio')}</p>
            ) : (
              <ul className="flex flex-col divide-y divide-borda">
                {d.itens.map((a) => (
                  <li key={a.id} className="flex items-center gap-3 py-2">
                    <Smartphone className="size-4 shrink-0 text-tinta-3" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <TextoCortado className="block text-sm text-tinta-1">{a.nome || t(`notificacoes.servicos.${a.servico}`)}</TextoCortado>
                      <span className="block text-xs text-tinta-3">
                        {t(`notificacoes.servicos.${a.servico}`)} · {a.ultimo_envio_em ? t('notificacoes.ultimoEnvio', { data: dataHora(a.ultimo_envio_em) }) : t('notificacoes.desde', { data: dataHora(a.created_at) })}
                      </span>
                    </div>
                    <Botao variante="fantasma" tamanho="sm" aria-label={t('notificacoes.remover', { nome: a.nome })} title={t('notificacoes.remover', { nome: a.nome })} carregando={remover.isPending && remover.variables === a.id} onClick={() => remover.mutate(a.id)} icone={<Trash2 className="size-4" aria-hidden />} />
                  </li>
                ))}
              </ul>
            )
          }
        </Estado>
      </div>
    </Cartao>
  );
}

/**
 * Fase 2 (W3): central de notificações em Minha conta (`/conta/notificacoes`): Caixa de entrada e Preferências.
 * B7 (lote 09/10, item 5): "Meus avisos" (`?aba=preferencias`, o que só vale para a pessoa: tipos, canais, som e aparelhos)
 * separado de "Avisos do workspace" (`?aba=workspace`, a antecedência que vale para a equipe toda; só a quem pode mudar).
 */
const ABAS = ['caixa', 'preferencias', 'workspace'] as const;
type Aba = (typeof ABAS)[number];

/** Lista de 30/10 (item 9): "Voltar" leva à tela anterior do sistema; aberta direto (sem histórico no app), vai ao Início. */
function BotaoVoltar() {
  const { t } = useTranslation();
  // Lista de 30/10 B (item 6): o mesmo ponto único dos outros Voltar (`useVoltar`).
  const { voltar } = useVoltar('/');
  return (
    <Botao variante="fantasma" tamanho="sm" className="self-start" icone={<ArrowLeft className="size-4" aria-hidden />} onClick={voltar} data-voltar-notificacoes="">
      {t('notificacoes.voltar')}
    </Botao>
  );
}

export function Notificacoes() {
  const { t } = useTranslation();
  const mostraAntecedencia = useMostraAntecedencia();
  const idAbas = useId();
  const [params, setParams] = useSearchParams();
  const abas = ABAS.filter((k) => k !== 'workspace' || mostraAntecedencia);
  const pedida = params.get('aba') as Aba | null;
  const aba: Aba = pedida && abas.includes(pedida) ? pedida : 'caixa';
  const opcoes = useOpcoes();
  // D2: ícones nas abas (ligados por padrão; só o desligar explícito do workspace tira).
  const icones = useIcones();
  useRecarregarNoSinal();
  return (
    <div className="flex flex-col gap-6">
      <BotaoVoltar />
      <header className="-mt-3 min-w-0">
        <p className="kicker">{t('notificacoes.kicker')}</p>
        <TextoCortado como="h1" className="mt-1 text-2xl font-bold tracking-tight">
          {t('notificacoes.titulo')}
        </TextoCortado>
        <SubtituloPagina caminho="/conta/notificacoes" />
      </header>
      <Abas idBase={idAbas} rotulo={t('notificacoes.titulo')} ativa={aba} aoMudar={(k) => setParams(k === 'caixa' ? {} : { aba: k }, { replace: true })} itens={abas.map((k) => ({ chave: k, rotulo: t(`notificacoes.abas.${k}`), icone: icones.de(`notificacoes.${k}`) }))} />
      <PainelAba idBase={idAbas} chave={aba}>
        {aba === 'caixa' ? (
          <Caixa />
        ) : aba === 'workspace' ? (
          <div className="flex flex-col gap-4" data-avisos-workspace>
            <p className="text-sm text-tinta-3">{t('notificacoes.workspaceTexto')}</p>
            {/* Fase 2 (W22, K8): a mesma antecedência de Contas > Cobranças (só aparece a quem pode mudar). */}
            <AntecedenciaAvisos linkCobrancas />
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2" data-meus-avisos>
            <div className="lg:col-span-2">
              <Preferencias />
            </div>
            <Estado consulta={opcoes}>{(o) => <OpcoesPessoais opcoes={o} />}</Estado>
            <Estado consulta={opcoes}>{(o) => <Aparelhos chave={o.push.chave_publica} />}</Estado>
          </div>
        )}
      </PainelAba>
    </div>
  );
}
